import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabase'
import { createLogger } from '../utils/logger'
import {
  clearOAuthUrlParams,
  getOAuthErrorFromUrl,
  hasOAuthUrlParams,
  isOAuthReturnPath,
} from '../utils/oauthCallback'

const logger = createLogger('auth')

/**
 * After OAuth, Supabase redirects to `redirectTo` (current origin + /login) with tokens
 * in the hash (or PKCE code in the query). Parse the session and send users to /app.
 */
export default function OAuthCallbackHandler() {
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (!hasOAuthUrlParams() || !isOAuthReturnPath(location.pathname)) {
      return
    }

    let finished = false

    const finish = (session) => {
      if (finished) return
      finished = true
      clearOAuthUrlParams()

      if (session) {
        logger.info('OAuth callback completed', { action: 'oauth_callback', view: 'app' })
        navigate('/app', { replace: true })
        return
      }

      const oauthError = getOAuthErrorFromUrl()
      if (oauthError) {
        logger.warn('OAuth callback failed', { action: 'oauth_callback', error: oauthError })
      }

      const loginPath = '/login'
      navigate(loginPath, {
        replace: true,
        state: oauthError ? { oauthError } : undefined,
      })
    }

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (session) {
          finish(session)
          return
        }
        if (getOAuthErrorFromUrl()) {
          finish(null)
        }
      })
      .catch((err) => {
        logger.error('Failed to parse OAuth session from URL', { action: 'oauth_callback' }, err)
        finish(null)
      })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        finish(session)
      }
    })

    return () => {
      finished = true
      subscription?.unsubscribe()
    }
  }, [location.pathname, navigate])

  return null
}
